import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { indianCities, jetModels, amenitiesList } from "./listingConstants";

/**
 * Jet Listing Form Component
 * 
 * Form for creating private jet seat listings with route,
 * seating, pricing, and amenities.
 */
export function AddJetForm({ 
  form, 
  setForm, 
  onSubmit, 
  loading, 
  selectedAmenities, 
  toggleAmenity 
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-6 max-w-2xl">
      {/* Origin & Destination */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Origin City</Label>
          <Select
            value={form.origin_city}
            onValueChange={(value) => setForm({ ...form, origin_city: value })}
          >
            <SelectTrigger className="rounded-none" data-testid="jet-origin-select">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              {indianCities.map((city) => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Destination City</Label>
          <Select
            value={form.destination_city}
            onValueChange={(value) => setForm({ ...form, destination_city: value })}
          >
            <SelectTrigger className="rounded-none" data-testid="jet-destination-select">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              {indianCities.map((city) => (
                <SelectItem key={city} value={city}>{city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Date & Time */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Departure Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start rounded-none" data-testid="jet-date-picker">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {form.departure_date ? format(form.departure_date, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={form.departure_date}
                onSelect={(date) => setForm({ ...form, departure_date: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="space-y-2">
          <Label>Departure Time</Label>
          <Input
            type="time"
            value={form.departure_time}
            onChange={(e) => setForm({ ...form, departure_time: e.target.value })}
            required
            className="rounded-none"
            data-testid="jet-time-input"
          />
        </div>
      </div>

      {/* Jet Model */}
      <div className="space-y-2">
        <Label>Jet Model</Label>
        <Select
          value={form.jet_model}
          onValueChange={(value) => setForm({ ...form, jet_model: value })}
        >
          <SelectTrigger className="rounded-none" data-testid="jet-model-select">
            <SelectValue placeholder="Select jet model" />
          </SelectTrigger>
          <SelectContent>
            {jetModels.map((model) => (
              <SelectItem key={model} value={model}>{model}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Seats & Pricing */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Available Seats</Label>
          <Input
            type="number"
            placeholder="e.g. 8"
            value={form.available_seats}
            onChange={(e) => setForm({ ...form, available_seats: e.target.value })}
            required
            className="rounded-none"
            data-testid="jet-seats-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Price per Seat (INR)</Label>
          <Input
            type="number"
            placeholder="e.g. 150000"
            value={form.price_per_seat}
            onChange={(e) => setForm({ ...form, price_per_seat: e.target.value })}
            required
            className="rounded-none"
            data-testid="jet-price-input"
          />
        </div>
      </div>

      {/* Amenities */}
      <div className="space-y-2">
        <Label>Amenities</Label>
        <div className="flex flex-wrap gap-2">
          {amenitiesList.map((amenity) => (
            <Button
              key={amenity}
              type="button"
              variant={selectedAmenities.includes(amenity) ? "default" : "outline"}
              size="sm"
              className="rounded-none"
              onClick={() => toggleAmenity(amenity)}
              data-testid={`amenity-${amenity.toLowerCase()}`}
            >
              {amenity}
            </Button>
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label>Description (Optional)</Label>
        <Textarea
          placeholder="Add details about your flight..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="rounded-none"
          data-testid="jet-description-input"
        />
      </div>

      {/* Contact Details */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Contact Phone</Label>
          <Input
            type="tel"
            value={form.contact_phone}
            onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            required
            className="rounded-none"
            data-testid="jet-phone-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Contact Email</Label>
          <Input
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            required
            className="rounded-none"
            data-testid="jet-email-input"
          />
        </div>
      </div>

      <Button 
        type="submit" 
        className="rounded-none btn-hover bg-amber-500 text-white hover:bg-amber-600"
        disabled={loading || !form.departure_date}
        data-testid="submit-jet-listing"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Jet Listing"}
      </Button>
    </form>
  );
}
